/**
 * @fileoverview Axios instance for the student portal.
 *
 * Same configuration as the instructor api.js but redirects to
 * /student/login on 401 instead of the landing page.
 */

import axios from "axios";

import BACKEND_URL from "./backendUrl";

const studentApi = axios.create({
  baseURL: `${BACKEND_URL}/api`,
  withCredentials: true,
});

studentApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (
      error.response?.status === 401 &&
      !window.location.pathname.startsWith("/student/login") &&
      !window.location.pathname.startsWith("/student/invite")
    ) {
      window.location.href = "/student/login";
    }
    return Promise.reject(error);
  }
);

export default studentApi;

/* ─── Practice logs (manual practice-time logging) ───────────────────────
 *
 * Mirror of the mobile client's getStudentPracticeLogs / createStudentPracticeLog /
 * updateStudentPracticeLog / deleteStudentPracticeLog in
 * mobile/src/services/apiService.js. Student-portal auth rides the HttpOnly
 * cookie on the axios instance above. The endpoints 404 when the
 * PRACTICE_LOGS_ENABLED kill switch is off — callers gate on
 * practiceLogsEnabled(student) from featureFlags before asking.
 */

/**
 * List the signed-in student's practice logs, newest first.
 * @param {import("axios").AxiosRequestConfig} [config] e.g. `{ signal }`.
 * @returns {Promise<Array<object>>}
 */
export async function listPracticeLogs(config = {}) {
  const { data } = await studentApi.get("/student/practice", config);
  return Array.isArray(data) ? data : data?.logs ?? [];
}

/**
 * Create a practice log.
 * @param {{date:string, duration_minutes:number, notes?:string|null, repertoire?:string|null, assignment_id?:string, practice_item?:string}} payload
 * @returns {Promise<object>} the created log (`log_id`, `date`, `duration_minutes`, `notes`, …).
 */
export async function createPracticeLog(payload) {
  const { data } = await studentApi.post("/student/practice", payload);
  return data;
}

/** Update fields on an existing practice log; returns the updated log. */
export async function updatePracticeLog(logId, payload) {
  const { data } = await studentApi.put(`/student/practice/${logId}`, payload);
  return data;
}

/** Delete a practice log. */
export async function deletePracticeLog(logId) {
  await studentApi.delete(`/student/practice/${logId}`);
}

/**
 * The student's practice streak, last eight weeks, totals and milestones (F3,
 * C7). `newly_awarded` arrives once, on the read that first notices it.
 * @param {import("axios").AxiosRequestConfig} [config]
 */
export async function getPracticeStats(config = {}) {
  const { data } = await studentApi.get("/student/stats", config);
  return data;
}

/**
 * Book a lesson (C6): one live public booking page per teacher, opened in a
 * new tab. Empty when no teacher has one; payment, if any, happens there.
 * @param {import("axios").AxiosRequestConfig} [config]
 * @returns {Promise<Array<{user_id:string, url:string, source:string, teacher_name?:string}>>}
 */
export async function getBookingLinks(config = {}) {
  const { data } = await studentApi.get("/student/booking-links", config);
  return data?.links || [];
}

/**
 * Extract a human-readable error message from an Axios error (mirrors
 * api.js's getErrorMessage). Handles structured details like the Phase 2
 * cancel gate's {code: "cutoff_passed", message, cutoff_hours}.
 */
export function getStudentErrorMessage(error, fallback = "Something went wrong") {
  const detail = error.response?.data?.detail;
  if (!detail) return fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((d) => d.msg || d.message || JSON.stringify(d)).join(", ");
  if (typeof detail.message === "string") return detail.message;
  return fallback;
}
